import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Grid,
  Stack,
  Alert,
  InputLabel,
} from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import moment from 'moment';

// COMPONENTS
import TemplateList from '../../components/templates/TemplateList';
import SectionWrapper from '../../components/common/SectionWrapper';
import ContactAutocomplete from '../../components/common/ContactAutocomplete';
import HelpTooltip from '../../components/common/HelpTooltip';
import SideNote from '../../components/common/SideNote';
import CustomModal from '../../components/common/CustomModal';

// LAYOUT
import Layout from '../../components/layouts/Layout';

// ROUTES
import { WebsmsRoutes } from '../../routes/WebsmsRoutes';

// REQUESTS
import useComposeRequest from '../../requests/ComposeRequest';

// UTIL
import {
  getCharacterCount,
  getMessageParts,
  getMessageMaxLength,
  withinCharacterLimit,
} from '../../utils/characterCount';

// MESSAGES
import ComposeFormattedMessages from '../../messages/websms/ComposeFormattedMessages';
import CreditsMessagesCounts from '../../components/common/CreditsMessagesCounts';

export default function Compose({
  customer,
  user,
  location,
  errorHandler,
  refreshUser,
}) {
  const [displayTemplatePopup, setDisplayTemplatePopup] = useState(false);
  const [noOfSmsSent, setNoOfSmsSent] = useState(0);
  const [tempPersonal, setTempPersonal] = useState(null);
  const [tempGlobal, setTempGlobal] = useState(null);
  const [messageContent, setMessageContent] = useState('');
  const [contacts, setContacts] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [loadingSelectedOption, setLoadingSelectedOption] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [scheduledTime, setScheduledTime] = useState(new Date());
  const [allowScheduling, setAllowScheduling] = useState(false);
  const [allowSignature, setAllowSignature] = useState(false);
  const [retainContent, setRetainContent] = useState(false);
  const [creditsTotal, setCreditsTotal] = useState(null);
  const [messagesTotal, setMessagesTotal] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [signature, setSignature] = useState('');
  const [autoCompleteInputValue, setAutoCompleteInputValue] = useState('');
  const [triggerNumberValidation, setTriggerNumberValidation] = useState('');
  const [messageCount, setMessageCount] = useState(0);
  const [validateTimeout, setValidateTimeout] = useState(null);
  const [intlCount, setIntlCount] = useState(0);
  const [subject, setSubject] = useState('');

  const {
    getNoSmsSentForUser,
    getSettings,
    getContacts,
    createSMS,
    validateNumbers,
  } = useComposeRequest();

  const messageContentRef = useRef(null);

  const handleContactsAndSettings = async () => {
    setIsLoading(true);
    // const user = await ComposeRequest.getUser(errorHandler);
    const customerUserSettings = await getSettings(errorHandler);
    const signature = customerUserSettings?.signature || '';

    const data = await getContacts(errorHandler);

    // Get list of personal contacts
    const personalContacts = data.contacts.personal.map((contact) => {
      return {
        label: `${contact.contact_name || ''}`,
        value: contact.number,
        type: 'Personal Contact',
        id: contact.sms_contact_id,
        status: null,
      };
    });

    // Get list of global contacts
    const globalContacts = data.contacts.global.map((contact) => {
      return {
        label: `${contact.contact_name || ''}`,
        value: contact.number,
        type: 'Global Contact',
        id: contact.sms_contact_id,
        status: null,
      };
    });

    // Get list of personal distribution lists
    const personalDistLists = data.distribution_lists.personal.map((list) => {
      return {
        label: `${list.list_name} (${list.countCount})`,
        value: `${list.distribution_list_id}`,
        type: 'Personal Distribution List',
        count: `${list.countCount}`,
      };
    });

    // Get list of global distribution lists
    const globalDistLists = data.distribution_lists.global.map((list) => {
      return {
        label: `${list.list_name} (${list.countCount})`,
        value: `${list.distribution_list_id}`,
        type: 'Global Distribution List',
        count: `${list.countCount}`,
      };
    });

    // Concatenate contacts together
    const contacts = globalContacts.concat(personalContacts);
    const distLists = globalDistLists.concat(personalDistLists);
    let selectableContacts = contacts.concat(distLists);

    selectableContacts = selectableContacts.sort((a, b) => {
      if (a.label.toLowerCase() < b.label.toLowerCase()) {
        return -1;
      }
      if (b.label.toLowerCase() < a.label.toLowerCase()) {
        return 1;
      }
      return 0;
    });

    setContacts(selectableContacts);
    setSignature(signature);
    setIsLoading(false);
    // setCountryCode(user.countryCode.country_code);
  };

  const handleOpenTemplateModal = () => {
    setDisplayTemplatePopup(!displayTemplatePopup);
  };

  const changeScheduledDateTime = (date) => {
    setScheduledTime(date);
  };

  const handleContactsChange = (_, contactValue, reason) => {
    const numberList = [];

    contactValue.map((contact) => {
      if (typeof contact === 'string') {
        // Contacts coming in as string if they are typed and entered via the "enter" key
        const response = handleTextEntry(contact);
        if (response) {
          numberList.push(...response);
        }
      } else if (!numberList.some((item) => item.id === contact.id)) {
        // These are contacts selected from the list. Manual entries that are already selected also show up here.
        numberList.push(contact);
      } else if (!numberList.some((item) => item.value === contact.value)) {
        // Manual numbers that are coming in as objects
        numberList.push(contact);
      }
    });

    setSelectedOptions(numberList);

    if (reason === 'removeOption' && numberList.length > 0) {
      if (validateTimeout) {
        clearTimeout(validateTimeout);
        setValidateTimeout(null);
      }
      setValidateTimeout(
        setTimeout(() => {
          setTriggerNumberValidation(true);
        }, 2000)
      );
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    if (!retainContent) {
      clearContent();
    } else {
      handleRetainContent();
    }
  };

  const handleInputBlur = (event) => {
    // Remove start and end blank
    const inputTn = event.target.value.trim();
    const numberList = inputTn ? handleTextEntry(inputTn) : [];

    // Prevents duplicate numbers from being added on-blur
    if (
      numberList[0] &&
      selectedOptions.some((item) => item.value === numberList[0].value)
    ) {
      return;
    }
    setSelectedOptions([...selectedOptions, ...numberList]);

    if (
      (!selectedOptions || !selectedOptions?.length) &&
      loadingSelectedOption
    ) {
      const locationState = location?.state;
      setSelectedOptions(locationState?.selectedOptions || []);
    }

    if (validateTimeout) {
      clearTimeout(validateTimeout);
      setValidateTimeout(null);
    }
    setValidateTimeout(
      setTimeout(() => {
        setTriggerNumberValidation(true);
      }, 2000)
    );
  };

  const showConfirmationAfterSent = () => {
    setShowConfirmation(true);
  };

  const clearContent = () => {
    setRetainContent(false);
    setTempPersonal(null);
    setTempGlobal(null);
    setShowConfirmation(false);
    setMessageContent('');
    setSelectedOptions([]);
    setAllowSignature(false);
    setAllowScheduling(false);
    setMessageCount(0);
    setIntlCount(0);
  };

  const handleRetainContent = () => {
    setRetainContent(true);
    setSelectedOptions([]);
  };

  const handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    switch (name) {
      case 'messageContent':
        setMessageContent(value);
        break;
      case 'allowSignature':
        setAllowSignature(value);
        break;
      case 'allowScheduling':
        setAllowScheduling(value);
        break;
      case 'retainContent':
        setRetainContent(value);
        break;
      default:
        break;
    }
  };

  const handleTemplateSelect = (element) => {
    setMessageContent(element.template_text);
    setDisplayTemplatePopup(false);
  };

  const sendMessage = () => {
    // If no mobile numbers selected
    if (!selectedOptions || selectedOptions?.length < 1) {
      setHasError(true);
      setErrorText('Selected Contact(s) is empty');
      // setIsCreatingSms(false);
      setIsSending(false);
      return;
    }
    // if no message content has been added...
    if (!messageContent) {
      setHasError(true);
      setErrorText('Message Content is empty');
      // setIsCreatingSms(false);
      setIsSending(false);
      return;
    }

    //If the scheduled date and time is in the past
    if (allowScheduling && scheduledTime < Date.now()) {
      setHasError(true);
      setErrorText('Date/Time invalid, please select a future Date/Time.');
      // setIsCreatingSms(false);
      setIsSending(false);
      return;
    }

    // setIsCreatingSms(true);
    setIsSending(true);

    (async () => {
      try {
        // For each selected option in the contacts selector...
        let message = messageContent;
        if (allowSignature) {
          message += signature ? '\n' + signature : '';
        }

        // Body data
        let data = {
          contacts: selectedOptions,
          message: message,
        };

        if (allowScheduling) {
          data.scheduled_datetime = scheduledTime;
        }

        if (subject) data.subject = subject;

        // Make request
        await createSMS(data, errorHandler);

        // Update this component and then request to update the User as well
        setHasError(false);
        // setIsCreatingSms(false);
        setIsSending(false);
        refreshUser();
        showConfirmationAfterSent();
      } catch (e) {
        setHasError(true);
        setErrorText(e.message);
        // setIsCreatingSms(false);
        setIsSending(false);
      }
    })();
  };

  const handleTextEntry = (inputTn) => {
    const numbers = [];
    // Else, create new
    if (inputTn.includes(',')) {
      const splitTns = inputTn.split(',');

      for (let i = 0; i < splitTns?.length; i++) {
        numbers.push({
          label: splitTns[i],
          value: splitTns[i],
          status: 'unverified',
          type: 'manual',
        });
      }
      return numbers;
    }

    numbers.push({
      label: inputTn,
      value: inputTn,
      status: 'unverified',
      type: 'manual',
    });
    return numbers;
  };

  const validateRecipientNumbers = useCallback(async () => {
    if (triggerNumberValidation) {
      const validated = await validateNumbers(
        { numbers: selectedOptions },
        errorHandler
      );
      if (validated) {
        setSelectedOptions(validated.result);
        setMessageCount(validated.messageCount);
        setIntlCount(validated.intlCount);
        setTriggerNumberValidation(false);
      }
    }
  }, [triggerNumberValidation]);

  useEffect(() => {
    validateRecipientNumbers();
  }, [validateRecipientNumbers]);

  const generateButtonsAndErrors = (
    creditsCost,
    messageCost,
    messageWithinLength
  ) => {
    // Returns no button but just text 'Sending...' if currently sending
    if (isSending) return <Typography>Sending...</Typography>;

    let messageDifference = messagesTotal - messageCost;
    let creditsDifference = creditsTotal - creditsCost;

    let errorTextMessage;
    let disabledSending = false;

    // If an error has been found and set to state, display below. These errors were not disabling sends before, so i've kept it that way.
    if (hasError) {
      errorTextMessage = errorText;
    }
    // If a pre-paid account and the cost of the send would go below the customer's current credits.
    else if (creditsTotal !== 'N/A' && creditsTotal < creditsCost) {
      disabledSending = true;
      errorTextMessage =
        'You have reached your message credit limit and, therefore, this message will not be sent. Please purchase more credits via the Web Administrator.';
    }
    // Else if the user's messages remaining for the day is less than the amount of messages they are attempting to send.
    else if (messagesTotal < messageCost) {
      disabledSending = true;
      errorTextMessage =
        'You have reached your daily message limit and, therefore, this message will not be sent. Please contact an Administrator to increase your daily messages.';
    }
    // Else if the message is not within the length limits.
    else if (!messageWithinLength) {
      disabledSending = true;
      errorTextMessage = `You’ve reached the message character count limit, this message cannot be sent.`;
    }
    // Else if there are 5 credits left, give a warning
    else if (creditsTotal && creditsTotal !== 'N/A' && creditsDifference <= 5) {
      errorTextMessage =
        'Warning: Your account has five credits or fewer remaining.';
    }
    // Else if there are 5 messages left, give a warning
    else if (messagesTotal && messageDifference <= 5) {
      errorTextMessage =
        'Warning: Your account has five messages or fewer remaining.';
    }

    return (
      <Stack sx={{ alignItems: { xs: 'center', md: 'flex-start' } }}>
        {/* If an error (or warning) to display, display it */}
        {errorTextMessage && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {errorTextMessage}
          </Alert>
        )}
        <Button
          variant="contained"
          color="secondary"
          onClick={sendMessage}
          disabled={disabledSending}
          sx={{ width: { xs: '50%', md: 'auto' } }}
        >
          {ComposeFormattedMessages.sendButton}
        </Button>
      </Stack>
    );
  };

  const handlePageReset = () => {
    setMessageContent('');
    setSelectedOptions([]);
    setAllowScheduling(false);
    setAllowSignature(false);
  };

  const calculateCreditsAndRecipients = (msgValue) => {
    let messagePartCount = getMessageParts(msgValue);

    let creditsCost = messagePartCount * (messageCount + intlCount);

    return {
      messagePartCount: messagePartCount,
      creditsCost: creditsCost,
      localRecipients: messageCount - intlCount,
      internationalRecipients: intlCount,
      allRecipients: messageCount,
    };
  };

  const messageWithSignature =
    messageContent + (allowSignature ? ' ' + signature : '');

  // Counts character for message (and signature if 'allowSignature' is true)
  const {
    standardCharacterOnly,
    unicodeCharacterOnly,
    characterCount,
    isUnicodeMessage,
  } = getCharacterCount(messageWithSignature);

  const {
    messagePartCount,
    creditsCost,
    localRecipients,
    internationalRecipients,
    allRecipients,
  } = calculateCreditsAndRecipients(messageWithSignature);

  const messageWithinLength = withinCharacterLimit(
    messageWithSignature,
    isUnicodeMessage
  );
  const messageMaxLength = getMessageMaxLength(isUnicodeMessage);

  useEffect(() => {
    if (!customer) return;

    // Handles redirect from inbox page.
    const selectedOptions = location.state?.selectedOptions || [];
    setSelectedOptions(selectedOptions);
    setLoadingSelectedOption(false);

    // Auto focuses the message content component if successful redirect.
    if (selectedOptions?.length > 0 && messageContentRef.current)
      messageContentRef.current.focus();

    const fetchData = async () => {
      const { count, creditCount, messageCount } = await getNoSmsSentForUser(
        errorHandler
      );

      setNoOfSmsSent(count || 0);
      setCreditsTotal(creditCount);
      setMessagesTotal(messageCount);
      handleContactsAndSettings();
    };

    fetchData();
  }, [customer, location.state, errorHandler]);

  // Formats the credits total and message total into strings if they weren't already.
  if (creditsTotal === null) setCreditsTotal('Loading...');
  else if (typeof creditsTotal === 'number')
    setCreditsTotal(
      new Intl.NumberFormat('en-US', {
        style: 'decimal',
        minimumFractionDigits: 1,
      }).format(creditsTotal)
    );

  if (!messagesTotal) setMessagesTotal('Loading...');
  else if (typeof messagesTotal === 'number')
    setMessagesTotal(new Intl.NumberFormat('en-US').format(messagesTotal));

  const showConfirmationDialog = () => {
    return (
      <CustomModal
        open={showConfirmation}
        setOpen={setShowConfirmation}
        onClose={handleCloseConfirmation}
        title={ComposeFormattedMessages.messageSend}
        width={500}
      >
        <Typography>
          {`${messageCount}/${messageCount} `}
          {ComposeFormattedMessages.messageQueued}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={retainContent}
                onChange={(e) => setRetainContent(e.target.checked)}
                name="retainContent"
              />
            }
            label="Retain Message Content"
          />

          <Button
            variant="contained"
            color="secondary"
            onClick={handleCloseConfirmation}
          >
            OK
          </Button>
        </Box>
      </CustomModal>
    );
  };

  return (
    <Layout routes={WebsmsRoutes} title={ComposeFormattedMessages.composeTitle}>
      <Grid container sx={{ justifyContent: 'space-between' }}>
        <Grid size={{ xs: 12, lg: 9 }}>
          <Grid container sx={{ gap: 2 }}>
            <Grid size={12}>
              <SectionWrapper
                title={ComposeFormattedMessages.recipientsTitle}
                icon="contact"
              >
                <InputLabel htmlFor="contacts">
                  {ComposeFormattedMessages.searchContactText}
                </InputLabel>

                <ContactAutocomplete
                  fullWidth
                  size="small"
                  id="contacts"
                  name="contacts"
                  loading={isLoading}
                  options={contacts}
                  getOptionLabel={(option) => option.label}
                  value={selectedOptions}
                  onChange={(event, newValue, reason) => {
                    handleContactsChange(event, newValue, reason);
                  }}
                  inputValue={autoCompleteInputValue}
                  onInputChange={(event, newInputValue) => {
                    clearTimeout(validateTimeout);
                    setValidateTimeout(null);
                    setAutoCompleteInputValue(newInputValue);
                  }}
                  onBlur={handleInputBlur}
                />
              </SectionWrapper>
            </Grid>

            <Grid size={12}>
              <SectionWrapper
                title={ComposeFormattedMessages.messageTitle}
                icon="compose"
              >
                <Stack
                  sx={{
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { sm: 'flex-end' },
                    gap: 2,
                    marginBottom: 2,
                  }}
                >
                  <InputLabel htmlFor="messageContent" sx={{ marginBottom: 0 }}>
                    {ComposeFormattedMessages.messageText}
                  </InputLabel>

                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleOpenTemplateModal}
                  >
                    {ComposeFormattedMessages.templateButton}
                  </Button>
                </Stack>

                <TextField
                  multiline
                  rows={3}
                  value={messageContent || ''}
                  id="messageContent"
                  name="messageContent"
                  onChange={(e) => handleInputChange(e)}
                  inputRef={messageContentRef}
                  slotProps={{
                    htmlInput: {
                      maxLength:
                        messageMaxLength -
                        (allowSignature && signature
                          ? signature.length + 1
                          : 0),
                    },
                  }}
                  fullWidth
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allowSignature}
                      onChange={(e) => handleInputChange(e)}
                      name="allowSignature"
                    />
                  }
                  label={`Signature: ${signature ? signature : ''}`}
                />

                <Box className="remindText">
                  <Stack sx={{ flexDirection: 'row' }}>
                    <Typography
                      sx={{
                        color: !messageWithinLength ? 'error.main' : 'inherit',
                        fontWeight: !messageWithinLength ? 700 : 400,
                      }}
                    >
                      <strong>{`${characterCount}/${messageMaxLength} Character(s)`}</strong>
                      {` = ${standardCharacterOnly} Standard Character(s)
                        ${
                          unicodeCharacterOnly > 0
                            ? ' + ' +
                              unicodeCharacterOnly +
                              ' Unicode Character(s)'
                            : ''
                        }`}
                    </Typography>

                    <HelpTooltip
                      message={
                        <Stack sx={{ gap: 2 }}>
                          <Typography>
                            1 message = 160 characters or less (including spaces
                            and signature character count).
                          </Typography>
                          <Typography>
                            If 160 characters is exceeded, the message will be
                            broken into parts, consisting of 153 characters.
                          </Typography>
                          <Typography>
                            We can support max. of 765(plain)/335(unicode)
                            characters = 5 parts.
                          </Typography>
                          <Typography>
                            1 unicode SMS = 70 unicode characters, any message
                            with 1 or more unicode characters is considered a
                            unicode message and will be counted as such. Emojis
                            are counted as unicode characters
                          </Typography>
                        </Stack>
                      }
                    />
                  </Stack>

                  <Stack sx={{ flexDirection: 'row' }}>
                    <Typography>
                      <strong>{`${creditsCost} Credit(s) Cost`}</strong>
                      {` = ${messagePartCount} Message Parts * (${localRecipients} Local Number${
                        localRecipients === 1 ? '' : 's'
                      } + ${internationalRecipients} International Number${
                        internationalRecipients === 1 ? '' : 's'
                      })`}
                    </Typography>

                    <HelpTooltip
                      message={
                        <Stack>
                          <Typography>Counting credits:</Typography>
                          <Typography>
                            For local messages (within your country code)
                          </Typography>
                          <Typography>1 part message = 1 credit</Typography>
                          <Typography>2 part message = 2 credits</Typography>
                          <Typography>3 part message = 3 credits</Typography>
                          <Typography>
                            International messages are 2 credits per part
                          </Typography>
                        </Stack>
                      }
                    />
                  </Stack>

                  <Typography fontWeight={700}>
                    {messageCount} Message(s)
                  </Typography>
                </Box>
              </SectionWrapper>
            </Grid>

            <Grid size={12}>
              <SectionWrapper title="Schedule Send" icon="schedule">
                <Stack sx={{ maxWidth: 450 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allowScheduling}
                        onChange={(e) => handleInputChange(e)}
                        name="allowScheduling"
                        color="secondary"
                      />
                    }
                    label={ComposeFormattedMessages.laterDateText}
                  />

                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimePicker
                      format="DD/MMM/YYYY h:mm A"
                      defaultValue={
                        !allowScheduling
                          ? moment(new Date())
                          : moment(scheduledTime)
                      }
                      fullWidth
                      onChange={(value) => changeScheduledDateTime(value)}
                      minDate={moment(new Date())}
                      disabled={!allowScheduling}
                      views={['year', 'month', 'day', 'hours', 'minutes']}
                      slotProps={{
                        textField: {
                          size: 'small',
                        },
                      }}
                    />
                  </LocalizationProvider>

                  <Stack
                    sx={{
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginTop: 1,
                    }}
                  >
                    <Typography fontSize={10}>
                      Users Timezone is {localStorage.getItem('timeString')}
                    </Typography>
                  </Stack>

                  <Stack sx={{ marginTop: 2, marginBottom: 1 }}>
                    <TextField
                      id="contactSearch"
                      label={'(Optional) Add Subject'}
                      variant="outlined"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      fullWidth
                      disabled={!allowScheduling}
                      size="small"
                    />
                  </Stack>
                </Stack>
              </SectionWrapper>
            </Grid>

            <Grid size={12} sx={{ paddingX: 4, paddingY: 0 }}>
              {generateButtonsAndErrors(
                creditsCost,
                messageCount,
                messageWithinLength
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid size={{ xs: 12, lg: 2.5 }}>
          {/*Comment the side note until it has been decided how to be managed*/}
          {/* <SideNote message="Notes could be added that are associated with each block. I&rsquo;d want them to disappear on mobile so they couldn&rsquo;t be anything mandatory for submission." /> */}
          <CreditsMessagesCounts
            creditsTotal={creditsTotal}
            messagesTotal={messagesTotal}
            sx={{
              marginTop: { xs: 5, lg: 10 },
            }}
          />
        </Grid>
      </Grid>

      <CustomModal
        setOpen={setDisplayTemplatePopup}
        open={displayTemplatePopup}
        title={ComposeFormattedMessages.chooseTemplateText}
        onClose={handleOpenTemplateModal}
        width={500}
        icon="templates"
      >
        <TemplateList
          handleTemplateSelect={handleTemplateSelect}
          customer={customer}
          errorHandler={errorHandler}
        />
      </CustomModal>

      {showConfirmationDialog()}
    </Layout>
  );
}
